import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Rating,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { mutation } from '../../../utils/api';
import { ApiRoutes } from '../../../constants/routes';
import { useColors } from '../../../constants/colors';

const ReviewForm = ({ contentType, contentId, existingReview, onSuccess, onCancel }) => {
  const Colors = useColors();
  const [title, setTitle] = useState(existingReview?.title || '');
  const [score, setScore] = useState(existingReview?.score || 5);
  const [reviewText, setReviewText] = useState(existingReview?.review_text || '');
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const createReviewMutation = useMutation({
    mutationFn: (data) => {
      const url = existingReview 
        ? ApiRoutes.MusicReviewDetail(existingReview.id)
        : ApiRoutes.MusicReviews;
      const method = existingReview ? 'PUT' : 'POST';
      return mutation(url, data, method);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['music-reviews', contentType, contentId]);
      if (onSuccess) onSuccess();
      setError(null);
    },
    onError: (err) => {
      setError(err.message || 'Failed to save review');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (reviewText.length < 10) {
      setError('Review text must be at least 10 characters long');
      return;
    }

    if (reviewText.length > 5000) {
      setError('Review text cannot exceed 5000 characters');
      return;
    }

    if (!title.trim()) {
      setError('Review title is required');
      return;
    }
    if (title.length > 200) {
      setError('Title cannot exceed 200 characters');
      return;
    }

    const contentTypeModel = contentType === 'track' ? 'track' : 'album';
    
    // Get ContentType ID
    const data = {
      content_type: contentTypeModel,
      object_id: contentId,
      title: title.trim(),
      score: score,
      review_text: reviewText,
      is_public: true
    };

    createReviewMutation.mutate(data);
  };

  return (
    <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.03)', borderRadius: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Fraunces', fontWeight: 'bold' }}>
        {existingReview ? 'Edit Your Review' : 'Write a Review'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Review Title (optional)
          </Typography>
          <TextField
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give your review a title... (required)"
            required
            inputProps={{ maxLength: 200 }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: Colors.mainYellow,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Rating (1-10)
          </Typography>
          <Rating
            value={score}
            onChange={(event, newValue) => {
              if (newValue !== null) {
                setScore(newValue);
              }
            }}
            max={10}
            size="large"
            sx={{
              '& .MuiRating-iconFilled': {
                color: Colors.mainYellow,
              },
              '& .MuiRating-iconEmpty': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          />
          <Typography variant="body2" sx={{ mt: 1, color: Colors.mainYellow }}>
            {score}/10
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={6}
          label="Review"
          placeholder="Write your review here (minimum 10 characters)..."
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          required
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: Colors.mainYellow,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          }}
        />

        <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'rgba(255, 255, 255, 0.6)' }}>
          {reviewText.length}/5000 characters
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={createReviewMutation.isLoading || reviewText.length < 10}
            sx={{
              backgroundColor: Colors.mainYellow,
              color: 'black',
              '&:hover': {
                backgroundColor: '#FFD700',
              },
            }}
          >
            {createReviewMutation.isLoading ? (
              <CircularProgress size={24} sx={{ color: 'black' }} />
            ) : (
              existingReview ? 'Update Review' : 'Submit Review'
            )}
          </Button>
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
              }}
            >
              Cancel
            </Button>
          )}
        </Box>
      </form>
    </Paper>
  );
};

export default ReviewForm;

